import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Lightbox from '../components/Lightbox';
import OrderForm from '../components/OrderForm';
import { imageUrl } from '../lib/images';
import { fetchCollection, fetchSide, FALLBACK_REFERENCER, FALLBACK_SIDER, getSlug } from '../lib/cms';
import { cn, getResponsiveCardSpan } from '../lib/utils';

export default function Referencer() {
  const [referencer, setReferencer] = useState(FALLBACK_REFERENCER);
  const [sideData, setSideData] = useState(FALLBACK_SIDER.referencer || {});
  const [selectedCategory, setSelectedCategory] = useState('alle');

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCaptions, setLightboxCaptions] = useState([]);
  const [currentImgIndex, setCurrentImgIndex] = useState(0);

  useEffect(() => {
    async function loadRefs() {
      const [refData, sData] = await Promise.all([
        fetchCollection('referencer', FALLBACK_REFERENCER),
        fetchSide('referencer')
      ]);
      if (Array.isArray(refData) && refData.length > 0) {
        setReferencer(refData);
      }
      if (sData) {
        setSideData(sData);
      }
    }
    loadRefs();
  }, []);

  const categories = ['alle', ...new Set(referencer.map((r) => r.opgavetype).filter(Boolean))];

  const filteredReferencer =
    selectedCategory === 'alle'
      ? referencer
      : referencer.filter((r) => r.opgavetype === selectedCategory);

  const openLightbox = (imgs, captions = [], index = 0) => {
    setLightboxImages(imgs);
    setLightboxCaptions(captions);
    setCurrentImgIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <PageMeta
        title={sideData.titel || "Referencer & Projekter - Duus Rustfri"}
        description={sideData.meta_beskrivelse || "Se udvalgte projekter og referencer udført af Duus Rustfri i Vissenbjerg. Specialfremstilling, foderkrybber, præcisionssvejsning og haveanlæg."}
      />

      {/* Hero Header */}
      <section className="bg-slate-950 text-white py-16 sm:py-24 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 border border-slate-800 text-sky-400 text-xs font-semibold uppercase tracking-wider mb-4">
              <i className="fa-solid fa-star text-sky-400" aria-hidden="true" />
              <span>{sideData.badge || 'Gennemførte opgaver'}</span>
            </div>
            <h1 className="text-3xl sm:text-5xl font-black tracking-tight text-white mb-4">
              {sideData.overskrift || 'Referencer & Projekter'}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
              {sideData.underoverskrift || 'Her kan du se et udvalg af konkrete projekter og smedeopgaver udført på vores værksted i Vissenbjerg.'}
            </p>
          </div>
        </div>
      </section>

      {/* Filter Tabs */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-semibold transition whitespace-nowrap cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-slate-900 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {cat === 'alle' ? 'Alle opgaver' : cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* References Grid */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredReferencer.map((ref, index) => {
              const primaryImg = ref.billede?.[0];
              const allImages = ref.billede || [];

              return (
                <div
                  key={ref.id || ref.slug}
                  className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col group h-full"
                >
                  {/* Photo area */}
                  <div
                    className="relative h-60 sm:h-64 bg-slate-900 overflow-hidden cursor-pointer shrink-0"
                    onClick={() => openLightbox(allImages, [ref.titel])}
                  >
                    <img
                      src={imageUrl(primaryImg, 'width=700,quality=85,format=auto')}
                      alt={ref.titel}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 bg-slate-950/80 px-3 py-1 rounded-full text-xs font-medium text-sky-400 border border-slate-800 backdrop-blur-sm">
                      {ref.opgavetype}
                    </div>

                    {allImages.length > 1 && (
                      <div className="absolute bottom-3 right-3 bg-slate-950/80 px-2.5 py-1 rounded-md text-xs font-mono text-white flex items-center gap-1.5 border border-slate-800">
                        <i className="fa-solid fa-images text-xs" aria-hidden="true" />
                        <span>{allImages.length} fotos</span>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="p-6 sm:p-8 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-3">
                      <h2 className="text-xl font-bold text-slate-950 group-hover:text-sky-700 transition">
                        <Link to={`/referencer/${getSlug(ref)}`}>{ref.titel}</Link>
                      </h2>

                      <div
                        className="text-sm text-slate-600 line-clamp-3 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: ref.beskrivelse }}
                      />
                    </div>

                    <div className="pt-3 border-t border-slate-100 space-y-1.5 text-xs text-slate-500">
                      {ref.materiale && (
                        <p>
                          <span className="font-semibold text-slate-700">Materiale:</span>{' '}
                          {ref.materiale}
                        </p>
                      )}
                      {ref.lokation && (
                        <p>
                          <span className="font-semibold text-slate-700">Lokation:</span>{' '}
                          {ref.lokation}
                        </p>
                      )}
                    </div>

                    <div className="pt-3 border-t border-slate-100 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5">
                      <Link
                        to={`/referencer/${getSlug(ref)}`}
                        className="text-sm font-semibold text-sky-700 hover:text-sky-800 transition inline-flex items-center justify-center sm:justify-start gap-1.5"
                      >
                        <span>Se projektet</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </Link>

                      <a
                        href="#bestil-lignende"
                        className="text-xs font-medium px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 transition text-center"
                      >
                        Forespørg på lignende
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section id="bestil-lignende" className="py-16 sm:py-24 bg-slate-950 text-white scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <OrderForm
            title="Ønsker du en lignende løsning?"
            subtitle="Send en beskrivelse eller vedhæft tegninger, så vender vi hurtigt tilbage med en skarp pris."
          />
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        isOpen={lightboxOpen}
        images={lightboxImages}
        captions={lightboxCaptions}
        currentIndex={currentImgIndex}
        onClose={() => setLightboxOpen(false)}
        onPrev={() => setCurrentImgIndex((prev) => (prev > 0 ? prev - 1 : lightboxImages.length - 1))}
        onNext={() => setCurrentImgIndex((prev) => (prev < lightboxImages.length - 1 ? prev + 1 : 0))}
        onSelectIndex={(idx) => setCurrentImgIndex(idx)}
      />
    </div>
  );
}
